import type {ComponentStageData, PluginContext} from '../../types'
import type {Pointer} from '@wix/document-services-types'
import {CTX_CONTENT_BOX, CTX_HORIZONTAL_MARGIN, CTX_HORIZONTAL_PADDING} from './constants'
import {getMeasurementsPointer, getTargetLayoutPointer} from '../../conversionDal/utils/pointer'
import {getParent, isInternalBox, isPlugin, updateTargetLayout} from '../common'
import {deepClone} from '@wix/wix-immutable-proxy'
import _ from 'lodash'

export const getFallbackRatio = ({componentPointer, initialSnapshot}: ComponentStageData) => {
    const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
    const originalMeasurements = initialSnapshot.getValue(componentMeasurementsPointer)
    return originalMeasurements.height / originalMeasurements.width
}

export const isInRange = (value: number, min: number, max?: number) => {
    return value >= min && value < (max ?? Infinity)
}

export const getContentBoxKey = (componentPointer: Pointer) => `${CTX_CONTENT_BOX}-${componentPointer.id}`

const getContentBox = (contextHelper: PluginContext, componentPointer: Pointer) => {
    return contextHelper.get(getContentBoxKey(componentPointer))
}

const getParentContentBox = (componentStageData: ComponentStageData, contextHelper: PluginContext): number => {
    const {componentPointer, conversionDal, config} = componentStageData

    if (isPlugin(componentStageData) && !isInternalBox(componentStageData)) {
        return config.targetCanvasSize
    }

    const parentPointer = getParent(conversionDal, componentPointer)
    return parentPointer ? getContentBox(contextHelper, parentPointer) : config.targetCanvasSize
}

const getHorizontalPaddingKey = (componentPointer: Pointer) => `${CTX_HORIZONTAL_PADDING}-${componentPointer.id}`

export const getHorizontalPadding = (contextHelper: PluginContext, componentPointer: Pointer): number => {
    return contextHelper.get(getHorizontalPaddingKey(componentPointer)) ?? 0
}

export const setHorizontalPadding = (contextHelper: PluginContext, componentPointer: Pointer, padding: number) => {
    contextHelper.set(getHorizontalPaddingKey(componentPointer), padding)
}

const getHorizontalMarginKey = (componentPointer: Pointer) => `${CTX_HORIZONTAL_MARGIN}-${componentPointer.id}`

const getHorizontalMargin = (contextHelper: PluginContext, componentPointer: Pointer): number => {
    return contextHelper.get(getHorizontalMarginKey(componentPointer)) ?? 0
}

export const writeHorizontalMarginToContext = (
    contextHelper: PluginContext,
    componentPointer: Pointer,
    margin: number
) => {
    contextHelper.set(getHorizontalMarginKey(componentPointer), margin)
}

export const getMaxWidth = (componentStageData: ComponentStageData, contextHelper: PluginContext): number => {
    const {componentPointer} = componentStageData
    return getParentContentBox(componentStageData, contextHelper) - getHorizontalMargin(contextHelper, componentPointer)
}

export const updateHorizontalMargins = (
    componentStageData: ComponentStageData,
    contextHelper: PluginContext,
    left: number,
    parentContentBox: number = getParentContentBox(componentStageData, contextHelper),
    stretchComponent: boolean = false,
    right: number = left
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer)))
    writeHorizontalMarginToContext(contextHelper, componentPointer, left + right)

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, itemLayout, {
            ...(stretchComponent && {
                justifySelf: 'stretch'
            }),
            margins: {
                left: {
                    value: (left / parentContentBox) * 100,
                    type: 'percentage'
                },
                right: {
                    value: (right / parentContentBox) * 100,
                    type: 'percentage'
                }
            }
        })
    })
}

export const updateComponentWidth = (
    componentStageData: ComponentStageData,
    contextHelper: PluginContext,
    width: number,
    parentContentBox: number = getParentContentBox(componentStageData, contextHelper)
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {componentLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {}
    const horizontalPadding = getHorizontalPadding(contextHelper, componentPointer)
    contextHelper.set(getContentBoxKey(componentPointer), width - horizontalPadding)

    updateTargetLayout(componentStageData, {
        componentLayout: {
            ...componentLayout,
            width: {
                value: (width / parentContentBox) * 100,
                type: 'percentage'
            },
            type: 'ComponentLayout'
        }
    })
}
